/**
 * @engine/input — Action Map
 *
 * A collection of related actions (e.g., "Gameplay", "UI", "Vehicle").
 * Can be enabled/disabled as a group.
 */

import type { ActionMapDef, ActionDef } from '../types';
import { InputAction } from './InputAction';

/**
 * Action Map — logical grouping of actions.
 */
export class ActionMap {
  /** Map name */
  readonly name: string;

  /** Actions in this map */
  readonly actions = new Map<string, InputAction>();

  /** Is this map enabled */
  private _enabled = true;

  constructor(def: ActionMapDef) {
    this.name = def.name;
    this._enabled = def.enabled ?? true;

    for (const actionDef of def.actions) {
      const action = new InputAction(actionDef);
      this.actions.set(action.name, action);
    }
  }

  // ============================================================================
  // Enable/Disable
  // ============================================================================

  get enabled(): boolean {
    return this._enabled;
  }

  set enabled(value: boolean) {
    if (this._enabled === value) return;
    this._enabled = value;

    // Propagate to all actions
    for (const action of this.actions.values()) {
      action.enabled = value;
    }
  }

  /**
   * Enable this map (convenience method)
   */
  enable(): void {
    this.enabled = true;
  }

  /**
   * Disable this map (convenience method)
   */
  disable(): void {
    this.enabled = false;
  }

  // ============================================================================
  // Action Access
  // ============================================================================

  /**
   * Get an action by name
   */
  getAction(name: string): InputAction | undefined {
    return this.actions.get(name);
  }

  /**
   * Check if an action exists
   */
  hasAction(name: string): boolean {
    return this.actions.has(name);
  }

  /**
   * Get all action names
   */
  getActionNames(): string[] {
    return Array.from(this.actions.keys());
  }

  /**
   * Iterate over all actions
   */
  [Symbol.iterator](): IterableIterator<InputAction> {
    return this.actions.values();
  }

  // ============================================================================
  // Frame Management
  // ============================================================================

  beginFrame(): void {
    for (const action of this.actions.values()) {
      action.beginFrame();
    }
  }

  endFrame(time: number): void {
    for (const action of this.actions.values()) {
      action.endFrame(time);
    }
  }

  reset(): void {
    for (const action of this.actions.values()) {
      action.resetState();
    }
  }

  // ============================================================================
  // Serialization
  // ============================================================================

  toDefinition(): ActionMapDef {
    return {
      name: this.name,
      actions: Array.from(this.actions.values()).map((a) => a.toDefinition()),
      enabled: this._enabled ? undefined : false,
    };
  }

  static fromDefinition(def: ActionMapDef): ActionMap {
    return new ActionMap(def);
  }

  // ============================================================================
  // Builder Pattern
  // ============================================================================

  /**
   * Add an action to this map
   */
  addAction(def: ActionDef): InputAction {
    if (this.actions.has(def.name)) {
      throw new Error(`Action '${def.name}' already exists in map '${this.name}'`);
    }

    const action = new InputAction(def);
    action.enabled = this._enabled;
    this.actions.set(def.name, action);
    return action;
  }

  /**
   * Remove an action from this map
   */
  removeAction(name: string): boolean {
    return this.actions.delete(name);
  }
}
